import React, { useState, useEffect } from 'react';
import { Plane, Download, Search, ArrowUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TicketTracker = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBalance, setFilterBalance] = useState('all');
  const [sortKey, setSortKey] = useState('employee_code');
  const [sortDir, setSortDir] = useState('asc');
  const [providedCount, setProvidedCount] = useState(0);
  const [pendingBulk, setPendingBulk] = useState(null); // true=enable all, false=disable all
  const [applying, setApplying] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/ticket-tracker`, { headers });
      setData(res.data.employees || []);
      setProvidedCount(res.data.provided_count || 0);
    } catch (err) {
      console.error('Failed to load ticket tracker', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allOn = data.length > 0 && providedCount === data.length;

  const applyBulk = async () => {
    if (pendingBulk === null) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/api/ticket-tracker/bulk-provided`,
        { provided: pendingBulk }, { headers });
      toast({
        title: pendingBulk ? 'Enabled for all' : 'Disabled for all',
        description: `Ticket entitlement ${pendingBulk ? 'turned ON' : 'turned OFF'} for ${res.data.modified} employee(s).`,
      });
      await load();
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setApplying(false);
      setPendingBulk(null);
    }
  };


  const filtered = data
    .filter(r => {
      const q = search.toLowerCase();
      if (q && !r.name?.toLowerCase().includes(q) && !r.employee_code?.toLowerCase().includes(q) && !r.department?.toLowerCase().includes(q)) return false;
      if (filterBalance === 'pending' && r.balance <= 0) return false;
      if (filterBalance === 'overdrawn' && r.balance >= 0) return false;
      if (filterBalance === 'settled' && r.balance !== 0) return false;
      return true;
    })
    .sort((a, b) => {
      let aVal = a[sortKey], bVal = b[sortKey];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortDir === 'asc'
        ? String(aVal || '').localeCompare(String(bVal || ''))
        : String(bVal || '').localeCompare(String(aVal || ''));
    });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleDownload = async (format) => {
    try {
      const response = await fetch(`${API}/api/ticket-tracker/download?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TicketTracker.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 3000);
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  const totalPending = data.filter(r => r.balance > 0).length;
  const totalOverdrawn = data.filter(r => r.balance < 0).length;
  const totalSettled = data.filter(r => r.balance === 0).length;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="ticket-tracker-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Plane className="h-5 w-5 text-sky-600" />
          <h1 className="text-xl font-bold text-gray-900">Flight Ticket Tracker</h1>
          <Badge variant="outline" className="text-xs">{data.length} employees</Badge>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => handleDownload('pdf')} data-testid="download-pdf-btn">
            <Download className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleDownload('excel')} data-testid="download-excel-btn">
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Bulk: Ticket Provided by Company — apply to all in one click */}
      <Card className="border border-sky-200 bg-sky-50/60 shadow-sm" data-testid="bulk-provided-panel">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-sm text-gray-900 flex items-center gap-2">
              <Plane className="h-4 w-4 text-sky-600" /> Ticket Provided by Company
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Turn the entitlement ON or OFF for <b>all {data.length} active employees</b> at once.
              Currently ON for <b>{providedCount}</b> of {data.length}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">All</span>
            <Switch
              checked={allOn}
              onCheckedChange={(v) => setPendingBulk(v)}
              data-testid="bulk-provided-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm" data-testid="stat-total">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 uppercase">Total</p>
            <p className="text-2xl font-bold text-gray-800">{data.length}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="stat-pending">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 uppercase">Tickets Pending</p>
            <p className="text-2xl font-bold text-amber-600">{totalPending}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="stat-overdrawn">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 uppercase">Overdrawn</p>
            <p className="text-2xl font-bold text-red-600">{totalOverdrawn}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="stat-settled">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 uppercase">Settled</p>
            <p className="text-2xl font-bold text-green-600">{totalSettled}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, code, or department..."
            className="pl-9"
            data-testid="ticket-search"
          />
        </div>
        <Select value={filterBalance} onValueChange={setFilterBalance}>
          <SelectTrigger className="w-[160px]" data-testid="ticket-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending (Due)</SelectItem>
            <SelectItem value="overdrawn">Overdrawn</SelectItem>
            <SelectItem value="settled">Settled (0)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ticket-table">
            <thead>
              <tr className="bg-gray-50 border-b">
                {[
                  { key: 'employee_code', label: 'Code' },
                  { key: 'name', label: 'Name' },
                  { key: 'department', label: 'Department' },
                  { key: 'join_date', label: 'Join Date' },
                  { key: 'frequency', label: 'Frequency' },
                  { key: 'eligible', label: 'Eligible' },
                  { key: 'used', label: 'Used' },
                  { key: 'balance', label: 'Balance' },
                  { key: 'last_ticket', label: 'Last Ticket' },
                  { key: 'provided', label: 'Provided' },
                ].map(col => (
                  <th
                    key={col.key}
                    className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
                    onClick={() => toggleSort(col.key)}
                    data-testid={`sort-${col.key}`}
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      {sortKey === col.key && <ArrowUpDown className="h-3 w-3" />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-gray-400">No matching employees</td>
                </tr>
              ) : (
                filtered.map((r, idx) => (
                  <tr key={r.id} className={`border-b hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`} data-testid={`ticket-row-${r.employee_code}`}>
                    <td className="px-3 py-2.5 font-mono text-xs text-gray-500">{r.employee_code}</td>
                    <td className="px-3 py-2.5 font-medium text-gray-900">{r.name}</td>
                    <td className="px-3 py-2.5 text-gray-600">{r.department || '-'}</td>
                    <td className="px-3 py-2.5 text-gray-600">{r.join_date || '-'}</td>
                    <td className="px-3 py-2.5">
                      <Badge variant="outline" className="text-[10px]">{r.frequency}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-center font-medium text-blue-600">{r.eligible}</td>
                    <td className="px-3 py-2.5 text-center text-gray-600">{r.used}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-flex items-center justify-center w-8 h-6 rounded text-xs font-bold ${
                        r.balance > 0
                          ? 'bg-amber-100 text-amber-700'
                          : r.balance < 0
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700'
                      }`}>
                        {r.balance}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-500">{r.last_ticket}</td>
                    <td className="px-3 py-2.5 text-center">
                      {r.provided ? (
                        <Badge className="bg-sky-100 text-sky-700 border-0 text-[10px]">ON</Badge>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={pendingBulk !== null} onOpenChange={(o) => { if (!o) setPendingBulk(null); }}>
        <AlertDialogContent data-testid="bulk-provided-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingBulk ? 'Enable ticket entitlement for everyone?' : 'Disable ticket entitlement for everyone?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will turn “Ticket Provided by Company” <b>{pendingBulk ? 'ON' : 'OFF'}</b> for all {data.length} active
              employees. You can still change individual employees afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-provided-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={applyBulk}
              disabled={applying}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="bulk-provided-apply"
            >
              {applying ? 'Applying…' : (pendingBulk ? 'Enable for all' : 'Disable for all')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TicketTracker;
